export const DEFAULT_DENOMINATION = 1;

/**
 * Level model of the jackpot PANELS. Level ids are 1-based and come from the jackpot stats poll
 * (`/api/jackpot/stats/internal` → `levelStats[].levelId`), which is a four-level model: a boost
 * jackpot's extra value rides along as `bonusValue` on levels 1 and 2 rather than as a level of
 * its own. Consumed by StandardJackpot.
 */
export const JACKPOT_LEVEL_IDS = {
  GRAND: 1,
  MAJOR: 2,
  MINOR: 3,
  MINI: 4,
} as const;

/** Level id by level key — the panel's `grand`/`major`/`minor`/`mini` row keys. */
export const JACKPOT_LEVEL_ID_BY_KEY: Record<string, number> = {
  grand: JACKPOT_LEVEL_IDS.GRAND,
  major: JACKPOT_LEVEL_IDS.MAJOR,
  minor: JACKPOT_LEVEL_IDS.MINOR,
  mini: JACKPOT_LEVEL_IDS.MINI,
};

/**
 * Uppercase display name by level id, for the panel rows. Not translated — the level names are
 * brand terms.
 */
export const JACKPOT_LEVEL_LABELS: Record<number, string> = {
  [JACKPOT_LEVEL_IDS.GRAND]: 'GRAND',
  [JACKPOT_LEVEL_IDS.MAJOR]: 'MAJOR',
  [JACKPOT_LEVEL_IDS.MINOR]: 'MINOR',
  [JACKPOT_LEVEL_IDS.MINI]: 'MINI',
};

/**
 * The level keys the statistics drawer has artwork and an accent colour for.
 */
export const JACKPOT_STATISTICS_LEVEL_KEYS = {
  GRAND: 'grand',
  MAJOR: 'major',
  MINOR: 'minor',
  MINI: 'mini',
} as const;

export type JackpotStatisticsLevelKey =
  (typeof JACKPOT_STATISTICS_LEVEL_KEYS)[keyof typeof JACKPOT_STATISTICS_LEVEL_KEYS];

/**
 * Display order of the statistics drawer's level tabs, keyed by NORMALISED level name (see
 * `normalizeJackpotLevelName`). The drawer's only ordering rule — `selectDisplayLevels`.
 */
export const JACKPOT_STATISTICS_LEVEL_RANK: Record<string, number> = {
  [JACKPOT_STATISTICS_LEVEL_KEYS.GRAND]: 10,
  super: 10,
  [JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR]: 20,
  middle: 20,
  [JACKPOT_STATISTICS_LEVEL_KEYS.MINOR]: 30,
  [JACKPOT_STATISTICS_LEVEL_KEYS.MINI]: 40,
};

/**
 * Rank for a level name {@link JACKPOT_STATISTICS_LEVEL_RANK} does not list. Sorts after every
 * known level instead of dropping the level; unknown names keep BE's own relative order, because
 * the sort is stable.
 */
export const JACKPOT_STATISTICS_UNRANKED_LEVEL = Number.MAX_SAFE_INTEGER;

export const JACKPOT_STATISTICS_BOOST_BASE_LEVELS: Record<string, string> = {
  boostgrand: 'Grand',
  boostmajor: 'Major',
};

/** The content panel's mutually exclusive ordering modes. */
export const JACKPOT_STATISTICS_SORT = {
  /** Wins ordered by date, newest first. Preselected when the drawer opens. */
  LAST_100: 'last_100',
  /** Wins ordered by amount, largest first. */
  TOP_10: 'top_10',
} as const;

/** How many rows the TOP 10 ordering shows — BE also sends at most this many `topWins` per level. */
export const JACKPOT_STATISTICS_TOP_WINS_LIMIT = 10;

/** How many rows the LAST 100 ordering shows — BE also sends at most this many `lastWins` per level. */
export const JACKPOT_STATISTICS_LAST_WINS_LIMIT = 100;

/**
 * Minor currency units (cents / stotinki) per major unit. Win amounts stay in BE's minor units
 * end to end; `formatCurrencyValue(amount, JACKPOT_WIN_MINOR_UNITS)` does the only conversion,
 * at render.
 */
export const JACKPOT_WIN_MINOR_UNITS = 100;

export const SECTIONS_WITH_JACKPOTS: { [key: string]: string } = {
  MYSTERY: 'MYSTERY',
  HIGH_CASH: 'HIGH_CASH',
  BELL_LINK: 'BELL_LINK',
  GODS_AND_KINGS: 'GODS_AND_KINGS',
  VIP_HIGH_CASH: 'VIP_HIGH_CASH',
  VIP_BELL_LINK: 'VIP_BELL_LINK',
  VIP_GODS_AND_KINGS: 'VIP_GODS_AND_KINGS',
  INSTANT_LINK: 'INSTANT_LINK',
  PROGRESSIVE: 'PROGRESSIVE',
  BELL_LINK_BOOST: 'BELL_LINK_BOOST',
  VIP_BELL_LINK_BOOST: 'VIP_BELL_LINK_BOOST',
  PREMIUM_LINK: 'PREMIUM_LINK',
};

export const JACKPOT_TYPES = {
  MYSTERY: 'Clover Chance',
  HIGH_CASH: 'High Cash',
  BELL_LINK: 'Bell Link',
  GODS_AND_KINGS: 'Gods and Kings',
  VIP_HIGH_CASH: 'VIP High Cash',
  VIP_BELL_LINK: 'VIP Bell Link',
  VIP_GODS_AND_KINGS: 'VIP Gods and Kings',
  INSTANT_LINK: 'InstantLink',
  PROGRESSIVE: 'progressive',
  BELL_LINK_BOOST: 'Bell Link Boost',
  VIP_BELL_LINK_BOOST: 'VIP Bell Link Boost',
  PREMIUM_LINK: 'Premium Link',
};
